// Project imports
import config from "./config.js";
import Utils from "./Utils.js";
import PlompClient from "./PlompClient.js";

// Global Stripe instance loaded from script tag
/* global Stripe */

/**
 * @typedef {Object} CartProduct
 * @property {string} id - Product identifier
 * @property {number} cost - Product cost
 * @property {string} description - Product description
 * @property {string} backgroundColour - Product background color
 * @property {string} title - Product title
 * @property {number} quantity - Quantity in cart
 */

/**
 * @typedef {Object} CartState
 * @property {Object.<string, CartProduct>} cart - Cart items by product ID
 * @property {number} [subTotal] - Cart subtotal
 * @property {number} [quantity] - Total quantity
 * @property {Object} [billingDetails] - Billing details from form
 * @property {string} [orderId] - Order ID from API
 * @property {string} [invoiceNumber] - Invoice number from API
 */

/**
 * @description A class to manage the PigLearn tokens project
 * @class Cart
 */
export default class Cart {
  // Private properties
  #lang;
  #Stripe;
  #stripeElements;
  #state = {
    cart: {},
  };
  #plompClient;

  /**
   * Creates an instance of Cart.
   * @memberof Cart
   */
  constructor(lang = "en-ca") {
    // Get the language from the HTML tag to use in locale-sensitive operations
    this.#lang = lang;
    this.#Stripe = Stripe(config.stripe.publicKey);
    this.#plompClient = new PlompClient(config.apiUrl, "", this.#lang);
  }

  /**
   * @description Bind handlers and generally manage the cardProducts accordion card
   * @memberof Cart
   */
  #cardProducts() {
    // Iterate the array of product cards and bind the quantity input change event
    [...document.querySelectorAll("#cardProducts .product")].forEach((cardProduct) => {
      // Bind an event handler to each product's quantity input field
      cardProduct.querySelector("input").addEventListener("change", (e) => {
        // RULE: We need at least one product in the cart to proceed to the next step
        /** @type {HTMLInputElement} */
        const target = /** @type {HTMLInputElement} */ (e.target);
        const productId = target.id;
        const quantity = parseInt(target.value);

        // Manage the #state object by adding/removing cart entries as required
        if (quantity > 0) {
          if (!this.#state.cart[productId]) {
            // Quantity is greater than zero and no entry exists so we need to initialise a cart entry
            this.#state.cart[productId] = { id: productId };
          }
          const product = config.products[productId];

          // Update the cart entry
          this.#state.cart[productId].cost = parseFloat(product.cost);
          this.#state.cart[productId].description = product.description;
          this.#state.cart[productId].backgroundColour = product.backgroundColour;
          this.#state.cart[productId].title = product.title;
          this.#state.cart[productId].quantity = quantity;
        } else {
          // Quantity is zero, we need to delete the cart entry
          delete this.#state.cart[productId];
        }

        // Recalculate the cart total and quantity
        this.#state.subTotal = Object.values(this.#state.cart).reduce((acc, product) => acc + product.cost * product.quantity, 0);
        this.#state.quantity = Object.values(this.#state.cart).reduce((acc, product) => acc + product.quantity, 0);

        // Check if any product has a quantity greater than zero
        const enableNextStep = [...document.querySelectorAll("#cardProducts .product input")].some(
          (input) => parseInt(/** @type {HTMLInputElement} */ (input).value) > 0
        );

        if (enableNextStep) {
          // Update the cart table and enable the next step
          this.#updateCartTable();
          this.#enableStep("2", true);
          this.#enableActionButton("2", true);
        } else {
          // Disable all steps after this one
          this.#enableStep("2", false);
          this.#enableActionButton("2", false);
          this.#enableStep("3", false);
        }
      });
    });
  }

  /**
   * @description Update the cart table on the billing address card
   * @memberof Cart
   */
  #updateCartTable() {
    // Get the tax rate for the currently selected province
    const select = /** @type {HTMLSelectElement} */ (document.querySelector("#frmProvince"));
    const taxRate = parseFloat(select.selectedOptions[0].getAttribute("data-tax") ?? "0");
    // Calculate the sub-total, tax and total
    const tax = this.#state.subTotal * taxRate;
    const total = this.#state.subTotal + tax;

    // Update the cart tables (one each in Step 2 and Step 3) with the new values
    document.querySelectorAll(".cartTable .cartQuantity").forEach((cartQuantity) => {
      cartQuantity.textContent = this.#state.quantity;
    });
    document.querySelectorAll(".cartTable .cartSubTotal").forEach((element) => {
      element.textContent = Utils.currency(this.#state.subTotal, this.#lang);
    });
    document.querySelectorAll(".cartTable .cartTaxes").forEach((element) => {
      element.textContent = Utils.currency(tax, this.#lang);
    });
    document.querySelectorAll(".cartTable .cartTotal").forEach((element) => {
      element.textContent = Utils.currency(total, this.#lang);
    });
  }

  /**
   * @description Check for form validity on the billing details card for every form field change, and call updateCartTable
   * @memberof Cart
   */
  #cardBillingDetails() {
    // Get a reference to the billing details form
    const billingDetailsForm = /** @type {HTMLFormElement} */ (document.getElementById("billingDetailsForm"));

    // Check form on each field input change and dispatch a custom event indicating form validity
    billingDetailsForm.addEventListener("input", () => {
      // It is brute force, but always calling updateCartTable is the easiest way to ensure we pick up any province changes for now
      this.#updateCartTable();

      if (billingDetailsForm.checkValidity()) {
        this.#enableActionButton("3", true);
      } else {
        this.#enableActionButton("3", false);
      }
    });
  }

  async #cardCheckout() {
    // Freeze the UI and build the cart payload at this point in time
    const billingForm = /** @type {HTMLFormElement} */ (document.getElementById("billingDetailsForm"));
    this.#state.billingDetails = Object.fromEntries(new FormData(billingForm));

    // Get the products and billing details from the forms as the payload to send to our API
    const productsForm = /** @type {HTMLFormElement} */ (document.getElementById("productsForm"));
    const payload = {
      products: Object.fromEntries(new FormData(productsForm)),
      billingDetails: this.#state.billingDetails,
      lang: this.#lang, // Send language at root level following web standards
    };

    return await this.#plompClient.createCart(payload);
  }

  /**
   * @description Render the access code cards for the user
   * @memberof Cart
   */
  async #cardSaveTokens() {
    const lang = this.#lang;
    // Retrieve the invoice data via the API
    const invoiceData = await Utils.getInvoice(this.#state.invoiceNumber, this.#state.orderId);
    if (!invoiceData) {
      throw new Error("Failed to retrieve invoice data");
    }

    invoiceData?.products?.forEach((product) => {
      if (!(product.accessCodes.length > 0)) {
        // Exit this loop as there are no access codes to render
        return;
      }

      // Stamp out each of the access code cards
      const accessCodeCardTemplate = /** @type {HTMLTemplateElement} */ (document.getElementById("accessCodeCardTemplate"));
      const accessCodeCardClone = /** @type {DocumentFragment} */ (accessCodeCardTemplate.content.cloneNode(true));

      /** @type {HTMLElement} */ (accessCodeCardClone.querySelector(".card-header")).style.cssText =
        `background-color: ${product.backgroundColour}; height:100px;`;
      /** @type {HTMLElement} */ (accessCodeCardClone.querySelector(".card-title")).textContent = lang.startsWith("fr")
        ? product.titleFr
        : product.title;
      /** @type {HTMLElement} */ (accessCodeCardClone.querySelector(".card-subtitle")).textContent = lang.startsWith("fr")
        ? `CA$${product.cost} (chaque)`
        : `CA$${product.cost} (each)`;
      /** @type {HTMLElement} */ (accessCodeCardClone.querySelector(".card-text")).textContent = lang.startsWith("fr")
        ? product.descriptionFr
        : product.description;

      product.accessCodes.map((accessCode) => {
        // Stamp out each of the access codes
        const accessCodeTemplate = /** @type {HTMLTemplateElement} */ (accessCodeCardClone.querySelector("#accessCodeTemplate"));
        const accessCodeClone = /** @type {DocumentFragment} */ (accessCodeTemplate.content.cloneNode(true));

        // Set the access code value
        /** @type {HTMLInputElement} */ (accessCodeClone.querySelector(".access-code")).value = accessCode;

        // Bind the copy button to the access code input
        accessCodeClone.querySelector("button .fa-copy").addEventListener("click", async (e) => {
          try {
            await navigator.clipboard.writeText(accessCode);
            // Optionally, provide feedback to the user
            const target = /** @type {HTMLElement} */ (e.target);
            target.classList.remove("fa-copy");
            target.classList.add("fa-check");
            setTimeout(() => {
              target.classList.remove("fa-check");
              target.classList.add("fa-copy");
            }, 2000);
          } catch {
            // Silently fail - clipboard API may not be available
          }
        });

        // Bind the eye icon to the access code reveal
        accessCodeClone.querySelector("button .fa-eye").addEventListener("click", async (e) => {
          const target = /** @type {HTMLElement} */ (e.target);
          const input = /** @type {HTMLInputElement} */ (target.closest(".input-group").querySelector("input"));
          if (input.type === "password") {
            // Swap the type to text to reveal the access code
            input.setAttribute("type", "text");
            // Swap the icon to the eye-slash
            target.classList.remove("fa-eye");
            target.classList.add("fa-eye-slash");
          } else {
            // Swap the type back to password to hide the access code
            input.setAttribute("type", "password");
            // Swap the icon back to the eye
            target.classList.remove("fa-eye-slash");
            target.classList.add("fa-eye");
          }
        });

        // Append the access code to the card body
        /** @type {HTMLElement} */ (accessCodeCardClone.querySelector(".card-body")).appendChild(accessCodeClone);
      });
      // Append the access code card to the row
      document.getElementById("cardSaveTokensRow").appendChild(accessCodeCardClone);
    });
  }

  /** PUBLIC METHODS */

  /**
   * @description Initialize the PigLearn tokens project event handlers and defaults
   * @param {Object} route - Router instance with query parameters
   * @param {boolean} [showCodes=false] - Whether to show access codes immediately
   * @returns {Promise<void>}
   * @memberof Cart
   * @public
   * @note This method is called from the Router class
   */
  async init(route, showCodes = false) {
    if (showCodes) {
      document.querySelectorAll(`button.step${1}`)[0].classList.add("disabled"); // This step doesn't have two buttons
      this.#state.orderId = route.query.orderid;
      this.#state.invoiceNumber = route.query.invoicenumber;
      // Bind handlers to enable accordion behaviour and checkout button
      this.#cardSaveTokens();

      const cards = [...document.querySelectorAll(".accordion > .card")];
      cards[0].querySelector(".collapse").classList.remove("show");
      cards[3].querySelector(".collapse").classList.add("show");
      cards[3].querySelector(".card-header button").scrollIntoView({ behavior: "smooth", block: "center" });
      document.querySelectorAll(`button.step${4}`)[0].classList.remove("disabled"); // This step doesn't have two buttons
      // Show this step and scroll it into view
    } else {
      // Bind handlers to enable accordion behaviour and checkout button
      this.#bindAccordionButtons();
      this.#bindStepButtons();
      this.#cardProducts();
      this.#cardBillingDetails();
    }
  }

  /**
   * @description Bind the card action buttons as well as the open/close click event to the accordion cards
   * @memberof Cart
   * @note The PayNow button does not get .step4 as it has its own unique behaviour
   */
  #bindAccordionButtons() {
    // Step rules
    // 1. The first step is always enabled
    // 2. The second step is enabled if the first step is valid
    // 3. The third step is enabled if the user chooses to continue in the second step, at which time steps 1 and 2 are disabled
    // 4. The fourth step is enabled if the user chooses to continue in the third step, at which time steps 1, 2 and 3 are disabled

    // Get the list of accordion cards (ignore embedded cards)
    const cards = [...document.querySelectorAll(".accordion > .card")];

    // Iterate each card
    cards.forEach((card) => {
      // prevCardOpener is the "next" button from the previous card
      const accordionButton = card.querySelector(".card-header button");

      // Bind the open/close click event to the card header button
      accordionButton?.addEventListener("click", (e) => {
        e.preventDefault();

        // If the card is closed, open it and close all others
        if (!card.querySelector(".collapse")?.classList.contains("show")) {
          document.querySelectorAll(`.accordion .collapse`).forEach((collapse) => {
            collapse.classList.remove("show");
          });

          // Show this step and scroll it into view
          card.querySelector(".collapse").classList.add("show");
          card.querySelector(".card-header button").scrollIntoView({ behavior: "smooth", block: "center" });
        } else {
          // Hide this step
          card.querySelector(".collapse").classList.remove("show");
        }
      });
    });
  }

  /**
   * @description Bind the action buttons that appear at the bottom of each step
   * @memberof Cart
   */
  #bindStepButtons() {
    // Continue to billing address is just an alias for the accordion click
    document.getElementById("btnContinueToBillingAddress").addEventListener("click", async (e) => {
      e.preventDefault();
      /** @type {HTMLButtonElement} */ (document.querySelector(".card-header button.step2")).click();
    });

    // Continue to checkout
    document.getElementById("btnContinueToCheckout").addEventListener("click", async (e) => {
      e.preventDefault();

      // Once the user moves to the checkout form they can't go back and edit the products or billing address
      document.querySelectorAll(`button.step${1}`)[0].classList.add("disabled"); // This step doesn't have two buttons
      this.#enableStep("2", false);
      this.#enableStep("3");

      // Start the async process to render the checkout form before we click to show it
      this.#renderCheckout();
      /** @type {HTMLButtonElement} */ (document.querySelector(".card-header button.step3")).click();
    });

    // Pay now
    document.getElementById("btnPayNow").addEventListener("click", async (e) => {
      e.preventDefault();
      await this.#checkout();
    });
  }

  async #renderCheckout() {
    // Bind handlers specific to managing the review card including a user-initiated checkout click

    // Get the orderId, invoiceNumber and stripeClientSecret from the API
    const { orderId, invoiceNumber, stripeClientSecret: clientSecret } = (await this.#cardCheckout()) ?? {};
    this.#state.orderId = orderId;
    this.#state.invoiceNumber = invoiceNumber;
    const appearance = {
      theme: "stripe",
    };
    // Stripe expects just the language part (e.g., "en" or "fr"), not the full BCP 47 tag
    const locale = this.#lang.split("-")[0];
    this.#stripeElements = this.#Stripe.elements({ locale, appearance, clientSecret });
    const paymentElementOptions = {
      layout: "tabs",
      // Tell the payment element to hide the postal code and country as we have already obtained these in the billing details form
      fields: {
        billingDetails: {
          address: {
            postalCode: "never",
            country: "never",
          },
        },
      },
    };
    const paymentElement = this.#stripeElements.create("payment", paymentElementOptions);
    paymentElement.mount("#payment-element");
  }

  async #checkout() {
    // eslint-disable-next-line max-len
    const return_url = `${config.stripe.callbackUrl}/${this.#lang.startsWith("fr") ? "fr/" : ""}?invoiceNumber=${this.#state.invoiceNumber}&orderId=${this.#state.orderId}`;

    const { error } = await this.#Stripe.confirmPayment({
      elements: this.#stripeElements,
      confirmParams: {
        payment_method_data: {
          // Since we hid these fields in #renderCheckout() we need to provide them here to confirm the payment
          billing_details: { address: { country: "CA", postal_code: this.#state.billingDetails.postalCode } },
        },
        return_url,
      },
    });

    // This code is only reached if the payment failed and Stripe did not redirect to the success URL
    if (error.type === "card_error" || error.type === "validation_error") {
      const div = document.getElementById("btnPayNow").closest(".card-body").querySelector("div.alert-danger");
      div.textContent = error.message;
      div.classList.remove("d-none");
    } else {
      const div = document.getElementById("#btnPayNow").closest(".card-body").querySelector("div.alert-danger");
      div.textContent = "An unexpected error occurred.";
      div.classList.remove("d-none");
    }
  }

  #enableActionButton(stepId, enable = true) {
    if (enable) {
      document.querySelectorAll(`button.step${stepId}`)[0].classList.remove("disabled");
    } else {
      document.querySelectorAll(`button.step${stepId}`)[0].classList.add("disabled");
    }
  }

  #enableStep(stepId, enable = true) {
    if (enable) {
      document.querySelectorAll(`button.step${stepId}`)[1].classList.remove("disabled");
    } else {
      document.querySelectorAll(`button.step${stepId}`)[1].classList.add("disabled");
    }
  }
}
